/**
 * La parte dello stato di autenticazione che rispecchia `GET /api/v1/auth/session`.
 *
 * Sta fuori da `AuthContext.tsx` per il limite di `CLAUDE.md` §3.3: il provider
 * è già il file più lungo dell'area, e la traduzione della risposta del gateway
 * in stato è una funzione pura, che si legge e si prova da sola.
 */

import {
  knownRoles,
  type ImpersonationState,
  type LoginMethod,
  type Role,
  type SessionResponse,
  type SessionStatus,
} from './authApi';

/** Utente autenticato, come lo vede la dashboard. */
export interface AuthenticatedUser {
  /** Nome utente nel realm. */
  username: string;
  /** Ruoli globali riconosciuti. */
  roles: Role[];
}

/** Parte dello stato che rispecchia la sessione del gateway. */
export interface SessionSnapshot {
  /** Stato della sessione. */
  status: SessionStatus;
  /** Utente autenticato, se c'è. */
  user: AuthenticatedUser | null;
  /** MFA obbligatoria per il ruolo. */
  mfaRequired: boolean;
  /** Secondo fattore già registrato. */
  mfaEnrolled: boolean;
  /** Come è nata la sessione, `null` se non si sa. */
  loginMethod: LoginMethod | null;
  /** Impersonazione in corso. */
  impersonation: ImpersonationState | null;
  /** La sessione può avviare un'impersonazione. */
  canImpersonate: boolean;
  /** Durata massima di un'impersonazione, in minuti. */
  impersonationMaxMinutes: number;
  /** True se l'avvio di un'impersonazione chiede il codice TOTP. */
  impersonationRequiresTotp: boolean;
}

/** Durata massima usata se il gateway non la dichiara. */
const DEFAULT_IMPERSONATION_MINUTES = 30;

/** Sessione assente: è anche lo stato di partenza e quello dopo il logout. */
export const ANONYMOUS: SessionSnapshot = {
  status: 'anonymous',
  user: null,
  mfaRequired: false,
  mfaEnrolled: false,
  loginMethod: null,
  impersonation: null,
  canImpersonate: false,
  impersonationMaxMinutes: DEFAULT_IMPERSONATION_MINUTES,
  impersonationRequiresTotp: false,
};

/**
 * Traduce la risposta del gateway nello stato della dashboard.
 *
 * I campi dell'impersonazione mancano nei gateway precedenti ad ADR 0009: si
 * leggono come «nessuna impersonazione» e «non può impersonare», che è la
 * posizione prudente.
 *
 * @param payload Risposta di `GET /api/v1/auth/session`.
 * @returns Lo stato corrispondente.
 */
export function toSnapshot(payload: SessionResponse): SessionSnapshot {
  return {
    status: payload.status,
    user:
      payload.username === null
        ? null
        : { username: payload.username, roles: knownRoles(payload.roles) },
    mfaRequired: payload.mfa_required,
    mfaEnrolled: payload.mfa_enrolled,
    loginMethod: payload.login_method ?? null,
    impersonation: payload.impersonation ?? null,
    canImpersonate: payload.can_impersonate === true,
    impersonationMaxMinutes: payload.impersonation_max_minutes ?? DEFAULT_IMPERSONATION_MINUTES,
    impersonationRequiresTotp: payload.impersonation_requires_totp === true,
  };
}
